import { SxProps, Theme } from '@mui/material/styles';

/* ------------------------------------------------------------------ */
/*  New Pharmacy Patient Modal – Styles                               */
/* ------------------------------------------------------------------ */

export const modalStyles: Record<string, SxProps<Theme>> = {
  /* Dialog paper */
  paper: {
    borderRadius: 4,
    maxWidth: 720,
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
  },

  /* Title bar */
  titleBar: {
    px: 4,
    pt: 3,
    pb: 2,
    borderBottom: '1px solid #E5E7EB',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
  },
  titleText: {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 700,
    fontSize: 20,
    color: '#1F2937',
  },
  closeBtn: {
    backgroundColor: '#F3F4F6',
    width: 36,
    height: 36,
    '&:hover': { backgroundColor: '#E5E7EB' },
  },

  /* Mode toggle radio group */
  modeRadioGroup: {
    mb: 2.5,
    columnGap: 3,
  },
  modeRadio: {
    color: '#E87040',
    '&.Mui-checked': { color: '#E87040' },
  },

  /* Content area */
  content: {
    px: 4,
    pt: 3,
    pb: 2,
  },

  /* ---- Existing patient card ---- */
  patientCard: {
    mt: 2.5,
    border: '1px solid #E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
    transition: 'box-shadow 0.25s ease',
    '&:hover': {
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.07)',
    },
  },
  cardHeader: {
    px: 2.5,
    py: 2,
    background: 'linear-gradient(135deg, #FAFAFF 0%, #F5F3FF 100%)',
    borderBottom: '1px solid #EDE9FE',
  },
  cardAvatarWrapper: {
    position: 'relative',
    width: 58,
    height: 58,
    flexShrink: 0,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #A5B4FC 0%, #818CF8 50%, #6366F1 100%)',
    p: '2px',
    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease',
  },
  cardAvatarWrapperHover: {
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.08)',
      boxShadow: '0 6px 20px rgba(99, 102, 241, 0.35)',
    },
  },
  cardAvatar: {
    width: '100%',
    height: '100%',
    fontSize: 18,
    fontWeight: 700,
    backgroundColor: '#EEF2FF',
    color: '#4F46E5',
    border: '2px solid #fff',
  },
  cardName: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  mrnChip: {
    mt: 0.5,
    height: 22,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: '#EEF2FF',
    color: '#4F46E5',
    borderRadius: 1,
    border: '1px solid #E0E7FF',
    '& .MuiChip-label': { px: 1 },
  },
  genderText: {
    fontSize: 11,
    fontWeight: 700,
    color: '#6366F1',
    backgroundColor: '#EEF2FF',
    border: '1px solid #C7D2FE',
    borderRadius: 2,
    px: 1.5,
    py: 0.4,
    lineHeight: 1.6,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 0,
  },
  cardGridItem: {
    px: 2.5,
    py: 1.8,
    borderBottom: '1px solid #F3F4F6',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#FAFAFF',
    },
    '&:nth-of-type(odd)': {
      borderRight: '1px solid #F3F4F6',
    },
    '&:last-child, &:nth-last-of-type(2):nth-of-type(odd)': {
      borderBottom: 'none',
    },
  },
  cardLabel: {
    fontSize: 11.5,
    color: '#9CA3AF',
    fontWeight: 500,
    mb: 0.5,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  cardValue: {
    fontSize: 14,
    color: '#1F2937',
    fontWeight: 600,
    wordBreak: 'break-word',
    lineHeight: 1.45,
  },

  /* ---- Action bar (bottom buttons) ---- */
  actionsBar: {
    px: 4,
    pb: 3,
    pt: 2.5,
    borderTop: '1px solid #E5E7EB',
  },
  actionsBtnRow: {
    display: 'flex',
    width: '100%',
    gap: 1.5,
    justifyContent: 'flex-end',
    flexDirection: { xs: 'column', sm: 'row' },
  },

  /* ---- Reusable button variants ---- */
  btnBase: {
    borderRadius: 2.5,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    px: 3,
    py: 1.1,
    minWidth: 120,
    fontFamily: 'Montserrat, sans-serif',
    transition: 'all 0.2s ease',
  },
  cancelBtn: {
    borderRadius: 2.5,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    px: 3,
    py: 1.1,
    minWidth: 120,
    fontFamily: 'Montserrat, sans-serif',
    transition: 'all 0.2s ease',
    color: '#374151',
    borderColor: '#D1D5DB',
    '&:hover': {
      borderColor: '#9CA3AF',
      backgroundColor: '#F9FAFB',
    },
  },
  /** "Save & Register as Patient" / "Save & Register (MRN)" – muted outlined */
  saveRegisterBtn: {
    borderRadius: 2.5,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    px: 3,
    py: 1.1,
    minWidth: 180,
    fontFamily: 'Montserrat, sans-serif',
    transition: 'all 0.2s ease',
    color: '#374151',
    backgroundColor: '#F3F4F6',
    borderColor: '#D1D5DB',
    '&:hover': {
      backgroundColor: '#E5E7EB',
      borderColor: '#9CA3AF',
    },
    '&.Mui-disabled': {
      backgroundColor: '#F9FAFB',
      color: '#D1D5DB',
      borderColor: '#E5E7EB',
    },
  },
  /* ---- Empty state (no patient selected) ---- */
  emptyState: {
    mt: 2.5,
    border: '1px dashed #D1D5DB',
    borderRadius: 3,
    p: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 180,
    backgroundColor: '#FAFAFA',
  },
  emptyStateTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#6B7280',
    mb: 0.5,
  },
  emptyStateHint: {
    fontSize: 13,
    fontWeight: 400,
    color: '#9CA3AF',
  },

  /** "Proceed" – dark primary */
  proceedBtn: {
    borderRadius: 2.5,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    px: 3,
    py: 1.1,
    minWidth: 120,
    fontFamily: 'Montserrat, sans-serif',
    transition: 'all 0.2s ease',
    backgroundColor: '#1F2937',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#374151',
    },
    '&.Mui-disabled': {
      backgroundColor: '#9CA3AF',
      color: '#fff',
    },
  },
};
